import { Link } from 'react-router-dom';
import { ArrowRight, Check, Cpu, KeyRound, Network, PackageCheck, Plug, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ThemeToggle';

const workflowSteps = [
  {
    step: '01 / Define',
    title: 'Make the rules explicit',
    description: 'Organize products and policies, choose entitlements, and set the activation and expiration rules your software needs.',
  },
  {
    step: '02 / Deploy',
    title: 'Connect the right hardware',
    description: 'Register device serials, review signatures, and manage license activations from the same workspace.',
  },
  {
    step: '03 / Operate',
    title: 'Keep the record together',
    description: 'Follow license activity, manage your team’s permissions, and connect your tools through API keys, webhooks, or MCP.',
  },
];

export default function Landing() {
  return (
    <main id="main-content">
      <div className="public-site">
        <header className="public-header">
          <Link to="/" className="brand-lockup od-row">
            <span className="brand-symbol"><Shield className="od-icon" /></span>
            <span>AirGap<span className="brand-suite"> Suite</span></span>
          </Link>
          <nav aria-label="Main navigation" className="od-row public-nav">
            <a href="#workflow">How it works</a>
            <Link to="/sdk-docs">Documentation</Link>
            <ThemeToggle />
            <Button asChild variant="outline">
              <Link to="/login">Sign in<ArrowRight className="od-icon ml-2" /></Link>
            </Button>
          </nav>
        </header>

        <section className="public-hero">
          <div className="hero-copy">
            <p className="page-eyebrow">Software licensing &middot; Online &amp; offline</p>
            <h1>
              Your software.<br />Your terms.<br />
              <span>Any network.</span>
            </h1>
            <p className="hero-description">
              Issue licenses, bind them to hardware, and keep verification working in disconnected environments. One workspace for the full license lifecycle.
            </p>
            <div className="od-cluster hero-actions">
              <Button asChild size="lg">
                <Link to="/register">Create a workspace<ArrowRight className="od-icon ml-3" /></Link>
              </Button>
              <Link to="/sdk-docs" className="text-link">
                Read the integration guide<ArrowRight className="od-icon" />
              </Link>
            </div>
            <div className="hero-assurances od-cluster">
              <span><Check className="od-icon" />Hardware binding</span>
              <span><Check className="od-icon" />Offline packages</span>
              <span><Check className="od-icon" />Scoped access</span>
            </div>
          </div>

          <div
            className="licensing-diagram"
            aria-label="Licensing workflow: issue a policy-based license, bind it to hardware, and verify online or using an offline package"
          >
            <div className="diagram-heading od-row">
              <span className="diagram-kicker od-fill">License lifecycle</span>
              <span className="diagram-label">Connected &rarr; Disconnected</span>
            </div>
            <div className="diagram-authority">
              <div className="od-row">
                <span className="diagram-icon"><KeyRound className="od-icon" /></span>
                <div className="od-field od-fill">
                  <span className="diagram-kicker">Issue</span>
                  <strong>One policy. Clear entitlements.</strong>
                </div>
              </div>
              <div className="diagram-attributes">
                <span>License type</span>
                <span>Activation limit</span>
                <span>Expiry</span>
              </div>
            </div>
            <div className="diagram-connector">
              <span>Hardware signature</span>
              <span className="diagram-line" />
            </div>
            <div className="diagram-device">
              <Cpu className="od-icon" />
              <div className="od-field">
                <span className="diagram-kicker">Bind</span>
                <strong>Authorized device</strong>
              </div>
              <span className="diagram-code">DEVICE &harr; LICENSE</span>
            </div>
            <div className="diagram-fork">
              <div className="diagram-path">
                <Network className="od-icon" />
                <span className="diagram-kicker">Online</span>
                <strong>Activate &amp; verify</strong>
                <p>Validate with the licensing service</p>
              </div>
              <div className="diagram-path">
                <PackageCheck className="od-icon" />
                <span className="diagram-kicker">Offline</span>
                <strong>Package &amp; verify</strong>
                <p>Transfer an encrypted verification package</p>
              </div>
            </div>
            <div className="diagram-footer">
              <Shield className="od-icon" />
              <span>Same license. Controlled access.</span>
            </div>
          </div>
        </section>

        <section id="workflow" className="public-workflow">
          <div>
            <p className="page-eyebrow">Designed for license operations</p>
            <h2>A clear path from<br />policy to deployment.</h2>
          </div>
          <div className="public-feature-list">
            {workflowSteps.map(({ step, title, description }) => (
              <article key={step}>
                <span className="feature-step">{step}</span>
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="public-integration">
          <Plug className="od-icon" />
          <div className="od-field od-fill">
            <h2>Build licensing into your product.</h2>
            <p>SDK guides, client downloads, and integration instructions are part of the workspace.</p>
          </div>
          <Button asChild variant="outline">
            <Link to="/sdk-docs">Explore the SDK<ArrowRight className="od-icon ml-2" /></Link>
          </Button>
        </section>

        <footer className="public-footer">
          <span>AirGap Suite</span>
          <span>Software license management</span>
          <Link to="/login">Open your workspace<ArrowRight className="od-icon" /></Link>
        </footer>
      </div>
    </main>
  );
}
